import { useEffect } from "react";
import { useForm } from "@tanstack/react-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useActiveStore } from "@/hooks/use-active-store";
import { useCreateCategory, useUpdateCategory } from "@/hooks/use-products";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category?: any;
  categories: any[];
};

const defaultValues = {
  name: "",
  slug: "",
  parent: 0,
  description: "",
};

function getDescendantIds(categories: any[], categoryId: number): number[] {
  const ids: number[] = [];
  function collect(parentId: number) {
    for (const c of categories) {
      if (c.parent === parentId) {
        ids.push(c.id);
        collect(c.id);
      }
    }
  }
  collect(categoryId);
  return ids;
}

export function CategoryDialog({ open, onOpenChange, category, categories }: Props) {
  const isEdit = !!category;
  const { storeId } = useActiveStore();
  const createMut = useCreateCategory(storeId);
  const updateMut = useUpdateCategory(storeId);

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      if (!value.name.trim()) {
        toast.error("Naziv kategorije je obavezan");
        return;
      }

      const payload: any = {
        name: value.name.trim(),
        parent: value.parent || 0,
      };
      if (value.slug.trim()) payload.slug = value.slug.trim();
      if (value.description.trim()) payload.description = value.description.trim();

      try {
        if (isEdit) {
          await updateMut.mutateAsync({ categoryId: category.id, ...payload });
          toast.success("Kategorija ažurirana");
        } else {
          await createMut.mutateAsync(payload);
          toast.success("Kategorija kreirana");
        }
        onOpenChange(false);
      } catch (err: any) {
        toast.error(err?.message || "Greška pri spremanju kategorije");
      }
    },
  });

  useEffect(() => {
    if (category) {
      form.reset({
        name: category.name || "",
        slug: category.slug || "",
        parent: category.parent || 0,
        description: category.description || "",
      });
    } else {
      form.reset(defaultValues);
    }
  }, [category, open]);

  const excludedIds = isEdit ? [category.id, ...getDescendantIds(categories, category.id)] : [];
  const parentOptions = categories.filter((c) => !excludedIds.includes(c.id));

  const isPending = createMut.isPending || updateMut.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm">{isEdit ? "Uredi kategoriju" : "Nova kategorija"}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className="flex flex-col gap-3"
        >
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Naziv kategorije *</Label>
            <form.Field name="name">
              {(field) => (
                <Input className="h-8 text-xs" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} placeholder="Naziv kategorije" />
              )}
            </form.Field>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Slug</Label>
            <form.Field name="slug">
              {(field) => (
                <Input className="h-8 text-xs font-mono" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} placeholder="automatski-generisan" />
              )}
            </form.Field>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Nadkategorija</Label>
            <form.Field name="parent">
              {(field) => (
                <Select value={String(field.state.value)} onValueChange={(v) => field.handleChange(Number(v))}>
                  <SelectTrigger className="h-8 text-xs w-full">
                    <SelectValue placeholder="Nema" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Nema</SelectItem>
                    {parentOptions.map((c: any) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </form.Field>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Opis</Label>
            <form.Field name="description">
              {(field) => (
                <Textarea className="text-xs min-h-[60px]" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} rows={3} placeholder="Opis kategorije (opcionalno)" />
              )}
            </form.Field>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Odustani
            </Button>
            <Button type="submit" size="sm" disabled={isPending}>
              {isPending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              {isEdit ? "Spremi" : "Kreiraj"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
